import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill'; // Rich Text Editor
import 'react-quill/dist/quill.snow.css'; // Editor styles
import apiClient from '../apiClient'; // Your authenticated API client
import { useParams, useNavigate } from 'react-router-dom';

// --- MUI Components ---
import {
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton
} from '@mui/material';

// --- MUI Icons ---
import { Save as SaveIcon, UploadFile as UploadFileIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';

export default function BlogForm() {
  const [website, setWebsite] = useState('horizonHauliers');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState(''); // This will be HTML
  const [category, setCategory] = useState('');
  const [authorName, setAuthorName] = useState('Admin');
  const [image, setImage] = useState(null); 
  const [imageName, setImageName] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  // --- Data Fetching ---
  useEffect(() => {
    if (isEditMode) {
      setLoading(true);
      setError(null);
      apiClient.get(`/blogs/${id}`)
        .then(response => {
          const blog = response.data;
          setWebsite(blog.website);
          setTitle(blog.title);
          setContent(blog.content); // Fetches the HTML content
          setCategory(blog.category);
          setAuthorName(blog.author.name);
        })
        .catch(err => {
          console.error(err);
          setError('Failed to load blog data. Please try again.');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [id, isEditMode]);

  // --- Form Handlers ---
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setImageName(file.name);
    } else {
      setImage(null);
      setImageName('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const formData = new FormData();
    formData.append('website', website);
    formData.append('title', title);
    formData.append('content', content); // Sends the HTML content
    formData.append('category', category);
    formData.append('authorName', authorName);
    
    if (image) {
      formData.append('image', image);
    }

    try {
      if (isEditMode) {
        await apiClient.put(`/blogs/${id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        if (!image) {
          setError('Please select an image for a new post.');
          setIsSubmitting(false);
          return;
        }
        await apiClient.post('/blogs', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      navigate('/admin/dashboard');
    } catch (err) {
      console.error('Error saving blog:', err);
      let errorMsg = 'Error saving blog: Please try again.';
      if (err.response) {
        if (err.response.status === 401 || err.response.status === 403) {
          errorMsg = 'Authentication failed. Please log out and log in again.';
        } else {
          errorMsg = `Error: ${err.response.data.message || err.message}`;
        }
      } else if (err.request) {
        errorMsg = 'Network Error: Could not connect to the server.';
      }
      setError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: { xs: 2, sm: 4 }, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: '10px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={() => navigate('/admin/dashboard')} aria-label="go back">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ ml: 1 }}>
            {isEditMode ? 'Edit Blog Post' : 'Create New Blog Post'}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                id="title"
                label="Blog Title"
                name="title"
                value={title}
                onChange={e => setTitle(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="website-select-label">Website</InputLabel>
                <Select
                  labelId="website-select-label"
                  id="website"
                  name="website"
                  label="Website"
                  value={website}
                  onChange={e => setWebsite(e.target.value)}
                >
                  <MenuItem value="horizonHauliers">Horizon Hauliers</MenuItem>
                  <MenuItem value="horizonEnergies">Horizon Energies</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                id="category"
                label="Category (e.g., Destination)"
                name="category"
                value={category}
                onChange={e => setCategory(e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="authorName"
                label="Author Name"
                name="authorName"
                value={authorName}
                onChange={e => setAuthorName(e.g.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} alignSelf="center">
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                fullWidth
              >
                Upload Image
                <input
                  type="file"
                  hidden
                  onChange={handleImageChange}
                  accept="image/*"
                />
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 1 }} noWrap>
                {imageName || (isEditMode ? "Leave blank to keep existing image" : "No file selected")}
              </Typography>
            </Grid>

            {/* --- Row 4: Rich Text Editor --- */}
            {/* 1. ✅ STYLING IMPROVEMENT: 
                This 'sx' prop targets ReactQuill's classes to add an MUI-like border.
            */}
            <Grid item xs={12} sx={{
              '& .ql-toolbar': { 
                borderRadius: '4px 4px 0 0', 
                borderColor: 'rgba(0, 0, 0, 0.23)' 
              },
              '& .ql-container': { 
                borderRadius: '0 0 4px 4px', 
                borderColor: 'rgba(0, 0, 0, 0.23)', 
                minHeight: '250px' 
              },
              '& .ql-container:hover': {
                borderColor: 'rgba(0, 0, 0, 0.87)'
              }
            }}>
              <FormControl fullWidth>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', pl: 1 }}>
                  Blog Content
                </Typography>
                <ReactQuill 
                  theme="snow" 
                  value={content} 
                  onChange={setContent}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              {/* 2. ✅ More margin-top to give the editor space */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 6 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => navigate('/admin/dashboard')}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                >
                  {isSubmitting ? 'Saving...' : 'Save Blog Post'}
                </Button>
              </Box>
            </Grid>

          </Grid>
        </Box>
      </Paper>
    </Box>
  );
}
